import React from 'react';
import { Badge } from './ui/badge';
import { Sparkles, Pencil, TrendingUp, Globe, ArrowRightLeft, ScanLine, FileUp, MapPin, Landmark } from 'lucide-react';

/**
 * CapitalSourceBadge — small visual indicator showing where a capital number
 * came from. Helps cashiers and managers understand WHY ₱20 vs ₱24 instead of
 * suspecting a bug.
 *
 * GLOBAL PRICING Phase 1 (2026-08-31): the badge used to recognize only four
 * source values, so every OTHER real writer (capital_propagation,
 * manual_override, pos_price_match, …) fell through to "Global" — the exact
 * mislabel the owner hit on Todo Max. Rules now:
 *   - "" / null                → "Global" (true fallback — no branch doc)
 *   - known source             → its own label
 *   - unknown NON-EMPTY source → "Branch" (branch-specific, origin unlabeled)
 * "Global" is never shown for a branch-specific number again.
 */

const SOURCE_META = {
  // live-derived
  derived_from_parent: { label: 'Live (parent)', tone: 'emerald', Icon: Sparkles,
    title: "Computed live from this branch's parent capital" },
  // purchase orders
  purchase_order: { label: 'PO', tone: 'sky', Icon: TrendingUp,
    title: 'Auto-updated from a Purchase Order receipt' },
  po_received: { label: 'PO', tone: 'sky', Icon: TrendingUp,
    title: 'Auto-updated from a Purchase Order receipt' },
  po_reopen_restore: { label: 'PO (restored)', tone: 'sky', Icon: TrendingUp,
    title: 'Restored when a Purchase Order was reopened' },
  overall_discount_backfill: { label: 'PO (discount)', tone: 'sky', Icon: TrendingUp,
    title: 'Adjusted from a Purchase Order overall discount' },
  // transfers & pickups
  branch_transfer: { label: 'Transfer', tone: 'indigo', Icon: ArrowRightLeft,
    title: 'Auto-updated from a Branch Transfer receipt' },
  transfer_received: { label: 'Transfer', tone: 'indigo', Icon: ArrowRightLeft,
    title: 'Auto-updated from a Branch Transfer receipt' },
  branch_transfer_repack: { label: 'Transfer (repack)', tone: 'indigo', Icon: ArrowRightLeft,
    title: 'Repack pricing applied from a Branch Transfer' },
  // manual decisions
  manual: { label: 'Manual', tone: 'slate', Icon: Pencil,
    title: 'Manually set as a branch override' },
  manual_override: { label: 'Manual', tone: 'slate', Icon: Pencil,
    title: 'Manually set as a branch override' },
  bulk_manual_override: { label: 'Manual (bulk)', tone: 'slate', Icon: Pencil,
    title: 'Manually set via a bulk price update' },
  global_manual_override: { label: 'Manual', tone: 'slate', Icon: Pencil,
    title: 'Manually set on the global product' },
  // propagation from Main
  capital_propagation: { label: 'Propagated', tone: 'violet', Icon: Landmark,
    title: "Propagated from Main Branch's capital (see note for details)" },
  // POS-originated
  pos_price_match: { label: 'Price match', tone: 'amber', Icon: ScanLine,
    title: 'Set by a POS price match during a sale' },
  pos_price_match_offline_replay: { label: 'Price match', tone: 'amber', Icon: ScanLine,
    title: 'Set by a POS price match replayed from an offline sale' },
  sales_jit: { label: 'POS', tone: 'amber', Icon: ScanLine,
    title: 'Saved at the point of sale (just-in-time repack retail)' },
  // repacks
  repack_create: { label: 'Repack', tone: 'emerald', Icon: Sparkles,
    title: 'Set when this repack product was created' },
  repack_pricing_manager: { label: 'Repack', tone: 'emerald', Icon: Sparkles,
    title: 'Set via the Repack Pricing manager' },
  // other writers
  audit_count_sheet: { label: 'Count sheet', tone: 'slate', Icon: MapPin,
    title: 'Set from an audit count sheet' },
  opening_anchor: { label: 'Opening', tone: 'slate', Icon: MapPin,
    title: 'Opening capital anchor' },
  closed_day_edit: { label: 'Edit (closed day)', tone: 'slate', Icon: Pencil,
    title: 'Adjusted by a closed-day invoice edit' },
  import: { label: 'Import', tone: 'slate', Icon: FileUp,
    title: 'Set by a data import' },
};

const TONES = {
  emerald: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  sky: 'bg-sky-100 text-sky-700 hover:bg-sky-100',
  indigo: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-100',
  slate: 'bg-slate-100 text-slate-700 hover:bg-slate-100',
  violet: 'bg-violet-100 text-violet-700 hover:bg-violet-100',
  amber: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
};

export default function CapitalSourceBadge({ source, dataTestId }) {
  let label, color, Icon, title;
  const meta = source ? SOURCE_META[source] : null;
  if (meta) {
    label = meta.label;
    color = TONES[meta.tone];
    Icon = meta.Icon;
    title = meta.title;
  } else if (source) {
    // Branch-specific but from a writer this badge doesn't know yet —
    // NEVER call it "Global": that mislabel is the bug this fixes.
    label = 'Branch';
    color = TONES.slate;
    Icon = MapPin;
    title = `Branch-specific value (source: ${source})`;
  } else {
    label = 'Global';
    color = TONES.amber;
    Icon = Globe;
    title = 'Falling back to the global product capital (no branch-specific data yet)';
  }
  return (
    <Badge className={`text-[9px] py-0 px-1.5 inline-flex items-center gap-1 font-normal ${color}`} title={title} data-testid={dataTestId}>
      <Icon size={9} /> {label}
    </Badge>
  );
}
